import { CalendarSource } from './calendar';
import { PresenceStatus, PresenceType } from './presence';
import { IResource } from './resource';
import { ScheduleType } from './schedule_type';
import { ISlot } from './slot';
import { IUserFull } from './user';
import { ScheduleVisibility } from './visibility';
import { ApprovalStatus } from './status';
import { HourType } from './hours';

/**
 * Preparation and cleanup time held around a reservation, on top of its own
 * period: nobody else can book the resource inside it. `null` on a schedule
 * means the reservation declares none and the office buffer applies instead.
 */
export type ScheduleBuffer = {
  before_minutes: number;
  after_minutes: number;
};

/** Longest buffer core accepts on either side of a reservation. */
export const MAX_SCHEDULE_BUFFER_MINUTES = 2880;

/**
 * What created a reservation, when it wasn't a person booking it directly.
 * `ticket` is a workplace request whose approval ran a `reservations:create`
 * workflow; `id` is that request's id. Only the gantt overview returns it so
 * far; every other schedule endpoint leaves it out.
 */
export interface ScheduleOrigin {
  type: 'ticket' | string;
  id: string;
}

export interface ISchedule {
  creator: IUserFull;
  organizer: IUserFull;
  creator_slots: number;
  description: string | null;
  end: string;
  has_checked_in: boolean;
  has_checked_out: boolean;
  id: string;
  organizer_id: string;
  is_accepted: boolean;
  is_pending: boolean;
  is_presence_accepted: boolean;
  is_presence_pending: boolean;
  message: null | string;
  presence_status: PresenceStatus;
  presence_type: PresenceType | null;
  resource: IResource;
  slot_id: string;
  slot_status: ApprovalStatus;
  slots: ISlot[];
  start: string;
  status: ApprovalStatus;
  team: null;
  time_zone: string;
  title: string | null;
  total_slots: number;
  total_users_accepted: number;
  total_users_pending: number;
  user: IUserFull;
  visibility: ScheduleVisibility | null;
  schedule_type: ScheduleType;
  recurring_task?: {
    id: string;
    type: 'schedule';
    rrules: string[];
    rrule: string;
    status: 'active';
    start: string;
    end: string;
  };
  recurring_schedule_id?: string;
  is_attendee?: boolean;
  is_obfuscated?: boolean;
  is_virtual?: boolean;
  buffer?: ScheduleBuffer | null;
  online_meeting?: {
    status: OnlineMeetingStatus;
    provider: OnlineMeetingProvider;
    url?: string;
  };
  origin?: ScheduleOrigin | null;
}

export type AvailabilityErrorCode =
  | 'overlaps_with_schedule'
  | 'overlaps_with_recurring_task'
  | 'office_future_limit'
  | 'invalid_resource'
  | 'resource_unavailable'
  | 'office_not_available'
  | 'resource_over_capacity'
  | 'concurrent_not_allowed'
  | 'resource_assignment_not_allowed'
  | 'concurrent_not_allowed_cross_office'
  | 'buffer_overlaps_with_schedule';

export type AvailabilityErrorDetails = AvailabilityErrorOverCapacityDetails | AvailabilityErrorOfficeFutureLimitDetails
  | AvailabilityErrorOfficeNotAvailableDetails | AvailabilityErrorResourceUnavailableDetails
  | AvailabilityErrorBufferOverlapDetails;

/**
 * The reservation whose buffer the requested period runs into. Core names it by
 * id only, so the title and times have to come from schedules already loaded.
 */
export type AvailabilityErrorBufferOverlapDetails = {
  schedule_id: string;
  recurring_task_id?: string;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
};

// One reason a resource (or the office) can't be reserved, as shown to the user.
export type AvailabilityError = {
  reasonCode: AvailabilityErrorCode;
  details?: AvailabilityErrorDetails;
};

export type AvailabilityErrorOverCapacityDetails = {
  resource_id: string;
  total_slots: number;
};

export type AvailabilityErrorOfficeFutureLimitDetails = {
  office_id: string;
  latest_allowed_date: string;
};

export type AvailabilityErrorHourDetails = {
  is_all_day: boolean;
  is_open: boolean;
  name: string;
  type: HourType;
  weekday?: string;
  date?: string;
  start_time?: string;
  end_time?: string;
  start_date?: string;
  end_date?: string;
};

export type AvailabilityErrorOfficeNotAvailableDetails = AvailabilityErrorHourDetails & {
  office_id: string;
};

export type AvailabilityErrorResourceUnavailableDetails = AvailabilityErrorHourDetails & {
  resource_id: string;
};

export type OnlineMeetingProvider = 'teams_for_business' | 'hangouts_meet';

// pending - meaning that link is not yet created
export type OnlineMeetingStatus = 'pending' | 'success' | 'error';